<template>
  <div id="app" class="h-screen flex flex-col">
    <!-- 地图容器 -->
    <div id="container" class="flex-1 w-full" style="height: calc(110vh - 110px);"></div>
    <div id="panel">
    <!-- 控制区域 - 整合为一行 -->
    <div class="bg-white px-4 py-3 shadow-md z-10 flex items-center justify-between flex-wrap gap-2" style="z-index:999">
      <!-- 左侧：用户ID和日期选择 -->
      <div class="flex items-center gap-3 flex-1 min-w-[300px]">
        <div class="flex-1 min-w-[150px]">
          <input 
            type="text" 
            v-model="userId" 
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            placeholder="ID"
          >
        </div>
        
        <div class="min-w-[140px]">
          <input 
            type="date" 
            v-model="selectedDate" 
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          >
        </div>
        
        <button 
          @click="fetchLocations" 
          class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm whitespace-nowrap"
        >
          <i class="fa fa-search mr-1"></i>获取
        </button>
      </div>
      
      <!-- 中间：时间筛选（有数据时显示） -->
      <div v-if="locationData.length" class="flex items-center gap-2 flex-1 min-w-[300px]">
        <div class="text-sm text-gray-600 whitespace-nowrap">时间：</div>
        
        <div class="min-w-[140px]">
          <input 
            type="time" 
            v-model="startTime" 
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
            :min="'00:00'"
            :max="endTime || '23:59'"
          >
        </div>
        
        <div class="text-gray-500">至</div>
        
        <div class="min-w-[140px]">
          <input 
            type="time" 
            v-model="endTime" 
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
            :min="startTime || '00:00'"
            :max="'23:59'"
          >
        </div>
        
        <button 
          @click="filterByTime" 
          class="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm whitespace-nowrap"
          :disabled="!isValidTimeRange"
        >
          <i class="fa fa-filter mr-1"></i>筛选
        </button>
      </div>
      
      <!-- 右侧：动画控制按钮 -->
      <div class="flex items-center gap-2">
        <button 
          @click="startAnimation" 
          class="px-3 py-2 bg-indigo-500 text-white rounded-md hover:bg-indigo-600 transition-colors text-sm whitespace-nowrap"
          :disabled="!filteredLocationData.length || isPlaying"
        >
          <i class="fa fa-play mr-1"></i>开始
        </button>
        
        <button 
          @click="pauseAnimation" 
          class="px-3 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 transition-colors text-sm whitespace-nowrap"
          :disabled="!isPlaying || isPaused"
        >
          <i class="fa fa-pause mr-1"></i>暂停
        </button>
        
        <button 
          @click="stopAnimation" 
          class="px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors text-sm whitespace-nowrap"
          :disabled="!isPlaying"
        >
          <i class="fa fa-stop mr-1"></i>停止
        </button>
      </div>
    </div>
    
    <!-- 状态提示 -->
    <div class="bg-gray-50 px-4 py-1.5 text-sm z-10 border-b border-gray-200">
      <template v-if="loading">
        <span class="text-blue-600"><i class="fa fa-spinner fa-spin mr-1"></i>加载中...</span>
      </template>
      <template v-if="errorMessage && !loading">
        <span class="text-red-500"><i class="fa fa-exclamation-circle mr-1"></i>{{ errorMessage }}</span>
      </template>
      <template v-if="locationData.length && !loading && !errorMessage">
        <span class="text-green-600">
          <i class="fa fa-check-circle mr-1"></i>
          原始: {{ locationData.length }} 点 | 筛选后: {{ filteredLocationData.length }} 点 | 显示标记: {{ displayMarkers.length }} 点
        </span>
      </template>
    </div>
    
    </div>

  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, nextTick, computed } from 'vue';
import axios from 'axios';
import qs from 'qs';

export default {
  name: 'MapPage',
  setup() {
    // 核心数据
    const userId = ref('72E59F7EF9A1D9DC54664D29E24F6DEA41BCD6DC');
    const selectedDate = ref(new Date().toISOString().split('T')[0]);
    const locationData = ref([]); // 原始数据
    const filteredLocationData = ref([]); // 时间筛选后数据
    const displayMarkers = ref([]); // 需显示的标记点索引（去重后）
    
    // 时间筛选
    const startTime = ref('00:00');
    const endTime = ref('23:59');
    
    // 状态控制
    const loading = ref(false);
    const errorMessage = ref('');
    const isPlaying = ref(false);
    const isPaused = ref(false);
    
    // 地图实例
    let map = null;
    let marker = null;
    let passedPolyline = null;
    let infoWindow = null;
    let pointMarkers = [];
    let fullPathPolyline = null;
    
    // 验证时间范围有效性
    const isValidTimeRange = computed(() => {
      if (!startTime.value || !endTime.value) return false;
      return startTime.value <= endTime.value;
    });

    // 初始化
    onMounted(() => {
      nextTick(() => loadAMapScript());
    });

    // 清理
    onUnmounted(() => {
      clearMap();
      if (map) map.destroy();
    });

    // 加载高德地图
    const loadAMapScript = () => {
      if (window.AMap) {
        initMap();
        return;
      }
      
      const script = document.createElement('script');
      script.src = 'https://webapi.amap.com/maps?v=2.0&key=356f5081c83428d5385d96bce195b991&plugin=AMap.MoveAnimation';
      script.async = true;
      script.onload = () => initMap();
      script.onerror = () => errorMessage.value = '地图加载失败';
      document.head.appendChild(script);
    };

    // 初始化地图
    const initMap = () => {
      if (!window.AMap) return;
      
      map = new window.AMap.Map('container', {
        resizeEnable: true,
        zoom: 13,
        center: [116.397428, 39.90923] // 默认北京
      });
      
      infoWindow = new window.AMap.InfoWindow({
        offset: new window.AMap.Pixel(0, -30),
        autoMove: true
      });
    };

    // 清除地图元素
    const clearMap = () => {
      if (isPlaying.value) stopAnimation();
      
      if (marker) marker.setMap(null);
      if (passedPolyline) passedPolyline.setMap(null);
      if (fullPathPolyline) fullPathPolyline.setMap(null);
      if (infoWindow) infoWindow.close();
      
      // 清除标记点
      pointMarkers.forEach(m => m.setMap(null));
      pointMarkers = [];
    };

    // 获取轨迹数据
    const fetchLocations = async () => {
      if (!userId.value || !selectedDate.value) {
        errorMessage.value = '请输入查询信息';
        return;
      }
      
      clearMap();
      loading.value = true;
      errorMessage.value = '';
      
      try {
        const response = await axios.post(
          'https://loc.budor.cn/getLocation',
          qs.stringify({ user: userId.value, time: selectedDate.value }),
          { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
        );
        
        if (response.data?.success) {
          locationData.value = response.data.data || [];
          
          if (locationData.value.length === 0) {
            errorMessage.value = '未找到数据';
            filteredLocationData.value = [];
            return;
          }
          
          // 排序
          locationData.value.sort((a, b) => new Date(a.location_time) - new Date(b.location_time));
          
          // 重置时间筛选为当天完整时段
          startTime.value = '00:00';
          endTime.value = '23:59';
          
          // 应用初始筛选
          filterByTime();
        } else {
          errorMessage.value = response.data?.msg || '获取失败';
          filteredLocationData.value = [];
        }
      } catch (err) {
        console.error('请求错误:', err);
        errorMessage.value = '网络错误，请重试';
        filteredLocationData.value = [];
      } finally {
        loading.value = false;
      }
    };

    // 按时间筛选数据
    const filterByTime = () => {
      clearMap();

      // 构建完整的筛选时间
      const filterStart = new Date(`${selectedDate.value}T${startTime.value}`);
      const filterEnd = new Date(`${selectedDate.value}T${endTime.value}`);

      // 筛选数据
      filteredLocationData.value = locationData.value.filter(item => {
        const itemTime = new Date(item.location_time);
        return itemTime >= filterStart && itemTime <= filterEnd;
      });

      if (filteredLocationData.value.length === 0) {
        errorMessage.value = '筛选范围内无数据';
        displayMarkers.value = [];
        return;
      }

      // 计算需要显示的标记点（修复：强制保留最后一个点）
      determineDisplayMarkers();

      // 绘制轨迹和标记
      drawTrace();
      drawPointMarkers();
    };

    /**
     * 修复后的标记点去重算法
     * 规则：
     * 1. 强制保留第一个点和最后一个点
     * 2. 中间点：如果与前一个保留点的经纬度差绝对值 ≥ 0.0002，则保留
     * 3. 避免重复保留最后一个点（如果已被中间点逻辑保留）
     */
    const determineDisplayMarkers = () => {
      const totalCount = filteredLocationData.value.length;

      // 边界情况：无数据/仅1个点
      if (totalCount === 0) {
        displayMarkers.value = [];
        return;
      }
      if (totalCount === 1) {
        displayMarkers.value = [0];
        return;
      }

      const markers = [0]; // 强制保留第一个点
      let lastIndex = 0; // 记录上一个保留点的索引
      const lastDataIndex = totalCount - 1; // 最后一个点的索引

      // 遍历中间点（1 ~ 倒数第二个点）
      for (let i = 1; i < lastDataIndex; i++) {
        // 获取经纬度数值
        const lastLng = parseFloat(filteredLocationData.value[lastIndex].longitude);
        const lastLat = parseFloat(filteredLocationData.value[lastIndex].latitude);
        const currLng = parseFloat(filteredLocationData.value[i].longitude);
        const currLat = parseFloat(filteredLocationData.value[i].latitude);

        // 计算差值绝对值
        const lngDiff = Math.abs(currLng - lastLng);
        const latDiff = Math.abs(currLat - lastLat);

        // 判断是否需要保留当前点
        if (lngDiff >= 0.0002 || latDiff >= 0.0002) {
          markers.push(i);
          lastIndex = i; // 更新上一个保留点的索引
        }
      }

      // 强制保留最后一个点（避免重复）
      if (!markers.includes(lastDataIndex)) {
        markers.push(lastDataIndex);
      }

      displayMarkers.value = markers;
    };

    // 绘制标记点（修复：替换样式，区分起点/终点/普通点）
    const drawPointMarkers = () => {
      if (!map || !filteredLocationData.value.length || !displayMarkers.value.length) return;

      const totalDisplayCount = displayMarkers.value.length;
      const lastMarkerIndex = displayMarkers.value[totalDisplayCount - 1]; // 最后一个显示点的原始索引

      displayMarkers.value.forEach((index, markerIdx) => {
        const item = filteredLocationData.value[index];
        let iconConfig = {};

        // 区分标记点样式
        if (markerIdx === 0) {
          // 起点样式：红色圆形标记
          iconConfig = new window.AMap.Icon({
            size: new window.AMap.Size(20, 20),
            image: 'https://a.amap.com/jsapi_demos/static/demo-center/icons/poi-marker-red.png',
            imageSize: new window.AMap.Size(20, 20)
          });
        } else if (index === lastMarkerIndex) {
          // 终点样式：绿色圆形标记
          iconConfig = new window.AMap.Icon({
            size: new window.AMap.Size(20, 20),
            image: 'https://a.amap.com/jsapi_demos/static/demo-center/icons/poi-marker-red.png',
            imageSize: new window.AMap.Size(20, 20)
          });
        } else {
          // 普通点样式：蓝色方形标记（替换原有样式）
          iconConfig = new window.AMap.Icon({
            size: new window.AMap.Size(16, 16),
            image: 'https://a.amap.com/jsapi_demos/static/demo-center/icons/dir-via-marker.png',
            imageSize: new window.AMap.Size(16, 16)
          });
        }

        const marker = new window.AMap.Marker({
          map: map,
          position: [parseFloat(item.longitude), parseFloat(item.latitude)],
          icon: iconConfig,
          zIndex: markerIdx === 0 || index === lastMarkerIndex ? 200 : 100, // 终点/起点层级更高
          extData: {
            index: index,
            type: markerIdx === 0 ? '起点' : (index === lastMarkerIndex ? '终点' : '普通点')
          }
        });

        pointMarkers.push(marker);

        // 点击事件（优化显示信息）
        marker.on('click', () => {
          const data = filteredLocationData.value[index];
          const markerType = marker.getExtData().type;
          infoWindow.setContent(`
            <div style="padding: 10px; min-width: 200px;">
              <p><strong>类型:</strong> ${markerType}</p>
              <p><strong>时间:</strong> ${data.location_time}</p>
              <p><strong>地址:</strong> ${data.address || '未知'}</p>
              <p><strong>经纬:</strong> ${data.longitude}, ${data.latitude}</p>
              <p><small>筛选后第${index + 1}/${filteredLocationData.value.length}点 | 显示第${markerIdx + 1}/${totalDisplayCount}点</small></p>
            </div>
          `);
          infoWindow.open(map, marker.getPosition());
        });
      });
    };

    // 绘制轨迹线
    const drawTrace = () => {
      if (!map || !filteredLocationData.value.length) return;

      // 轨迹点数组（使用所有筛选后的数据点，用于动画和完整轨迹）
      const path = filteredLocationData.value.map(item => [
        parseFloat(item.longitude),
        parseFloat(item.latitude)
      ]);

      // 完整轨迹线
      fullPathPolyline = new window.AMap.Polyline({
        map: map,
        path: path,
        showDir: true,
        strokeColor: "#28F",
        strokeOpacity: 1,
        strokeWeight: 6
      });

      // 已走过的轨迹
      passedPolyline = new window.AMap.Polyline({
        map: map,
        strokeColor: "#A75",
        strokeOpacity: 1,
        strokeWeight: 6
      });

      // 移动标记
      marker = new window.AMap.Marker({
        map: map,
        position: path[0],
        icon: "https://webapi.amap.com/theme/v1.3/markers/n/start.png",
        offset: new window.AMap.Pixel(-12, -6),
        autoRotation: true,
        zIndex: 200
      });

      // 移动事件
      marker.on('moving', e => {
        passedPolyline.setPath(e.passedPath);
      });

      // 调整地图视野
      map.setFitView([fullPathPolyline]);
    };

    // 动画控制方法
    const startAnimation = () => {
      if (marker && filteredLocationData.value.length && !isPlaying.value) {
        const path = filteredLocationData.value.map(item => [
          parseFloat(item.longitude),
          parseFloat(item.latitude)
        ]);

        marker.moveAlong(path, 20);
        isPlaying.value = true;
        isPaused.value = false;
      }
    };

    const pauseAnimation = () => {
      if (marker && isPlaying.value && !isPaused.value) {
        marker.pauseMove();
        isPaused.value = true;
      }
    };

    const resumeAnimation = () => {
      if (marker && isPlaying.value && isPaused.value) {
        marker.resumeMove();
        isPaused.value = false;
      }
    };

    const stopAnimation = () => {
      if (marker && isPlaying.value) {
        marker.stopMove();
        isPlaying.value = false;
        isPaused.value = false;

        // 重置到起点
        const path = filteredLocationData.value.map(item => [
          parseFloat(item.longitude),
          parseFloat(item.latitude)
        ]);
        marker.setPosition(path[0] || [0, 0]);
        passedPolyline.setPath([]);
      }
    };

    return {
      userId,
      selectedDate,
      locationData,
      filteredLocationData,
      displayMarkers,
      startTime,
      endTime,
      loading,
      errorMessage,
      isPlaying,
      isPaused,
      isValidTimeRange,
      fetchLocations,
      filterByTime,
      startAnimation,
      pauseAnimation,
      resumeAnimation,
      stopAnimation
    };
  }
};
</script>

<style scoped>
#app {
  overflow: hidden;
}

#container {
  width: 100%;
  height: 100%;
}

/* 响应式调整 */
@media (max-width: 992px) {
  .flex-wrap {
    row-gap: 8px;
  }
  
  .flex-1 {
    min-width: 100% !important;
    justify-content: center;
  }
  
  .flex.items-center {
    justify-content: center;
  }
}

/* 优化按钮和输入框样式一致性 */
input, button {
  box-sizing: border-box;
  height: 36px;
}

button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
#panel {
  position: fixed;
  background-color: white;
  opacity: 0.7;
  max-height: 90%;
  overflow-y: auto;
  top: 10px;
  right: 10px;
  width: 180px;
}
#panel .amap-call {
  background-color: #009cf9;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
}
#panel .amap-lib-driving {
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  overflow: hidden;
}
</style>